import React, { useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import './CreateTeamModal.css';

interface TeamMember {
  name: string;
}

interface CreateTeamModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

const CreateTeamModal: React.FC<CreateTeamModalProps> = ({ show, onHide, onSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [newOperatorName, setNewOperatorName] = useState('');
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [canEditBuoni, setCanEditBuoni] = useState(false);
  const [canDeleteBuoni, setCanDeleteBuoni] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAddMember = () => {
    const name = newOperatorName.trim();
    if (name) {
      setMembers([...members, { name }]);
      setNewOperatorName('');
    }
  };

  const handleRemoveMember = (index: number) => {
    setMembers(members.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== passwordConfirmation) {
      setError('Le password non corrispondono');
      return;
    }

    if (password.length < 8) {
      setError('La password deve essere di almeno 8 caratteri');
      return;
    }

    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${API_URL}/admin/teams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          username: username.trim(),
          password,
          members: members.length > 0 ? members : null,
          can_edit_buoni: canEditBuoni,
          can_delete_buoni: canDeleteBuoni,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors) {
          const errorMessages = Object.values(data.errors).flat().join(', ');
          throw new Error(errorMessages || data.message || 'Errore durante la creazione');
        }
        throw new Error(data.message || 'Errore durante la creazione');
      }

      setUsername('');
      setPassword('');
      setPasswordConfirmation('');
      setNewOperatorName('');
      setMembers([]);
      setCanEditBuoni(false);
      setCanDeleteBuoni(false);
      onSuccess();
      onHide();
    } catch (err: any) {
      setError(err.message || 'Errore durante la creazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered className="create-user-modal">
      <div className="create-user-modal-box">
        <div className="create-user-modal-inner">
          {/* Header */}
          <div className="create-user-modal-header">
            <h2 className="create-user-modal-title">Crea Utente</h2>
            <button
              type="button"
              className="create-user-modal-close"
              onClick={onHide}
              aria-label="Chiudi"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2 2l12 12M14 2L2 14" />
              </svg>
            </button>
          </div>

          <Form onSubmit={handleSubmit} className="create-user-form">
            <div className="create-user-form-body">
              {error && <Alert variant="danger" className="create-user-alert">{error}</Alert>}

              <div className="create-user-field">
                <label className="create-user-label">Nome Utente</label>
                <Form.Control
                  type="text"
                  placeholder="Nome"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="create-user-input"
                  required
                />
              </div>

              <div className="create-user-field">
                <label className="create-user-label">Password</label>
                <Form.Control
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="create-user-input"
                  required
                  minLength={8}
                />
              </div>

              <div className="create-user-field">
                <label className="create-user-label">Conferma Password</label>
                <Form.Control
                  type="password"
                  placeholder="••••••••"
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  className="create-user-input"
                  required
                  minLength={8}
                />
              </div>

              <div className="create-user-field">
                <label className="create-user-label">Aggiungi operatori</label>
                <div className="create-user-add-row">
                  <Form.Control
                    type="text"
                    placeholder="Nome"
                    value={newOperatorName}
                    onChange={(e) => setNewOperatorName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddMember())}
                    className="create-user-input create-user-input-flex"
                  />
                  <Button type="button" className="create-user-btn-add" onClick={handleAddMember}>
                    +
                  </Button>
                </div>
                {members.length > 0 && (
                  <div className="create-user-members-list">
                    {members.map((m, index) => (
                      <div key={index} className="create-user-member-chip">
                        <span>{m.name}</span>
                        <button type="button" onClick={() => handleRemoveMember(index)} aria-label="Rimuovi">×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="create-user-permessi">
                <label className="create-user-label create-user-permessi-title">Permessi</label>
                <div className="create-user-permessi-row">
                  <span className={`create-user-checkbox ${canEditBuoni ? 'checked' : ''}`} />
                  <span
                    className="create-user-permessi-label"
                    onClick={() => setCanEditBuoni(!canEditBuoni)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setCanEditBuoni(!canEditBuoni)}
                  >
                    Modifica Buono
                  </span>
                </div>
                <div className="create-user-permessi-row">
                  <span className={`create-user-checkbox ${canDeleteBuoni ? 'checked' : ''}`} />
                  <span
                    className="create-user-permessi-label"
                    onClick={() => setCanDeleteBuoni(!canDeleteBuoni)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setCanDeleteBuoni(!canDeleteBuoni)}
                  >
                    Elimina Buono
                  </span>
                </div>
              </div>
            </div>

            <div className="create-user-footer">
              <Button type="button" variant="light" className="create-user-btn-annulla" onClick={onHide} disabled={loading}>
                Annulla
              </Button>
              <Button type="submit" className="create-user-btn-submit" disabled={loading}>
                {loading ? 'Creazione...' : 'Crea Utente'}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default CreateTeamModal;
